import { Session } from '@aspendental/shared-components-web';
import { getSessionId } from '@aspendental/shared-utils/utils';
import { useMarketingSource } from '@aspendental/web-shared-utils';
import * as Sentry from '@sentry/nextjs';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { ContextProviders } from '@src/components/shared/providers/ContextProviders';
import { VWOSmartCode } from '@src/components/shared/third-party/analytics-vwo/VWOSmartCode';
import { Layout } from '@src/components/templates/layout';
import { getLogger } from '@src/lib/logging/logger';
import { isGhostScheduleTreatment, isPhoneSwapTreatment } from '@src/lib/vwo-fme';
import { initializeLogRocket } from '@src/pages/utils/logRocketInit';
import { isLogRocketEnabled, toggleLogRocket } from '@src/services/logRocket';
import { isSentryEnabled, toggleSentry } from '@src/services/sentry';
import { initializeSentryClient } from '@src/util/sentryClientInit';

import '@aspendental/shared-components-web/lib/dynamic-form.css';
import '@aspendental/shared-components-web/lib/fonts.css';
import '@aspendental/shared-components-web/lib/global.css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/pagination';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/navigation';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css';

export function reportWebVitals({ id, name, label, value }: NextWebVitalsMetric) {
	const logger = getLogger('client');
	logger.info({
		name,
		category: label === 'web-vital' ? 'Web Vitals' : 'Next.js custom metric',
		value: Math.round(name === 'CLS' ? value * 1000 : value), // values must be integers
		id, // id unique to current page load
	});
}

type AppPropsWithLayout = AppProps & {
	Component: NextPageWithLayout;
};

const App = ({ Component, pageProps }: AppPropsWithLayout) => {
	const router = useRouter();
	const config = pageProps?.config as BrandThemeConfig;
	const { features, featureFlags } = config;
	const brandName = config?.name || '';
	toggleSentry(!!pageProps?.config?.features?.sentry?.enabled);
	toggleLogRocket(!!features?.logRocket?.enabled);
	const brazeEnabled = config.features.analytics.braze.enabled;
	const brazeSdkKey = config.features.analytics.braze.sdkKey;
	const brazeBaseUrl = config.features.analytics.braze.baseUrl;
	const brazeLoggingEnabled = config.features.analytics.braze.enableLogging;

	const disableStickyFooterOnDesktop = pageProps.page?.disableStickyFooterOnDesktop;
	const disableStickyFooterOnMobile = pageProps.page?.disableStickyFooterOnMobile;
	const disableHeaderTopAlertBanner = pageProps.page?.disableHeaderTopAlertBanner;
	const disableHeaderBottomAlertBanner = pageProps.page?.disableHeaderBottomAlertBanner;
	const showAspen2Header = featureFlags?.featureList?.includes('showAspen2Header') ?? false;
	const showClearChoice2Header = featureFlags?.featureList?.includes('showClearChoice2Header') ?? false;

	// VWO `ccPatientPrismPhoneSwap` 1% treatment, resolved server-side via
	// resolveFeatureFlags(LAYOUT_FEATURE_FLAGS, ...) and forwarded as
	// serverFeatureFlags. `isPhoneSwapTreatment` isolates the non-control arm
	// (see its doc for why `isEnabled` alone would swap ~100% instead of 1%).
	// Defaults to off (control) on any route that doesn't wire it up or on error.
	// Threaded into Layout to swap the national ClearChoice header CTA number.
	const phoneSwapOn = isPhoneSwapTreatment(pageProps?.serverFeatureFlags?.ccPatientPrismPhoneSwap);

	// VWO `ghostPrimaryScheduleButton` (TTB-322) treatment, resolved server-side
	// via resolveFeatureFlags(LAYOUT_FEATURE_FLAGS, ...) and forwarded as
	// serverFeatureFlags. Same non-control gate as phoneSwapOn (variationId > 1).
	// When on, the ClearChoice2 header renders its primary CTA as a ghost button.
	const ghostPrimaryScheduleButton = isGhostScheduleTreatment(
		pageProps?.serverFeatureFlags?.ghostPrimaryScheduleButton
	);

	const environment = pageProps?.env?.ENVIRONMENT_NAME || 'prod';

	useEffect(() => {
		if (brazeEnabled) {
			import('@src/pages/utils/braze-exports').then(
				({ initialize, openSession, automaticallyShowInAppMessages }) => {
					initialize(brazeSdkKey, {
						baseUrl: brazeBaseUrl,
						enableLogging: brazeLoggingEnabled,
						allowUserSuppliedJavascript: true,
					});
					automaticallyShowInAppMessages();
					openSession();
				}
			);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [brazeEnabled]);

	useEffect(() => {
		if (isSentryEnabled()) {
			initializeSentryClient(environment);
			Sentry.setTag('brand', brandName);
			Sentry.setAttribute('brand', brandName);
			Sentry.setContext('user', {
				sessionId: getSessionId(),
				brand: brandName,
			});
		}
	}, [brandName, environment]);

	useEffect(() => {
		if (isLogRocketEnabled() && features?.logRocket?.projectId) {
			initializeLogRocket(environment, features.logRocket.projectId);
		}
	}, [brandName, environment, features?.logRocket?.projectId]);

	// Capture marketing_source cookie from landing URL query params.
	useMarketingSource({ enableCcTransformation: featureFlags?.enableCcMarketingSourceTransformation ?? false });

	// Dev-only: warn if a page mounts the default Layout but its
	// getServerSideProps did not populate `serverFeatureFlags`. Without this
	// wiring, layout-level VWO flags (e.g. showNewHeader) silently default to
	// off on that route regardless of the experiment.
	useEffect(() => {
		if (
			process.env.NODE_ENV !== 'production' &&
			!Component.getLayout &&
			pageProps?.serverFeatureFlags === undefined
		) {
			console.warn(
				'[VWO FME] pageProps.serverFeatureFlags is undefined on a page that mounts the default Layout. ' +
					"Add `resolveFeatureFlags(LAYOUT_FEATURE_FLAGS, ...)` to this page's getServerSideProps; " +
					'layout-level flags will fall back to disabled.'
			);
		}
	}, [Component, pageProps?.serverFeatureFlags]);

	useEffect(() => {
		// when component is mounting we remove server side rendered css:
		const jssStyles = document.querySelector('#jss-server-side');

		jssStyles?.parentNode?.removeChild(jssStyles);

		router.events.on('routeChangeComplete', () => null);

		return () => {
			router.events.off('routeChangeComplete', () => null);
		};
	}, [router.events]);
	const getLayout =
		Component.getLayout ??
		((page) => {
			return (
				<Layout
					locale={router.locale || 'en-US'}
					preview={pageProps.preview}
					appData={pageProps.appData}
					isFooterLite={features.footer.lite}
					isFooterAlt={features.footer.alt}
					withSimpleLayout={features.footer.withSimpleLayout}
					footerData={pageProps.footerData}
					showHeaderNavMenu={featureFlags?.showHeaderNavigationMenu || false}
					breadcrumbsData={pageProps.breadcrumbsData}
					withFixedHeader={!features.header.hideHeaderOnScroll}
					disableStickyFooterOnDesktop={disableStickyFooterOnDesktop}
					disableStickyFooterOnMobile={disableStickyFooterOnMobile}
					hideLocation={featureFlags?.hideHeaderFacilityLocator ?? false}
					hidePrimaryActionButton={
						featureFlags?.hideHeaderSchedulingButton ?? pageProps.page?.hideScheduleButtonInHeader ?? false
					}
					disableHeaderTopAlertBanner={disableHeaderTopAlertBanner}
					disableHeaderBottomAlertBanner={disableHeaderBottomAlertBanner}
					showAspen2Header={showAspen2Header}
					showClearChoice2Header={showClearChoice2Header}
					phoneSwapOn={phoneSwapOn}
					ghostPrimaryScheduleButton={ghostPrimaryScheduleButton}
				>
					{page}
				</Layout>
			);
		});
	const vwoAccountId = features?.analytics?.vwoAccountId;
	const loadVWOFromGTM = featureFlags?.featureList?.includes('loadVWOFromGTM');

	return (
		<Session>
			{features?.analytics?.enabled && !loadVWOFromGTM && vwoAccountId && (
				<VWOSmartCode accountId={vwoAccountId} />
			)}
			<ContextProviders
				config={pageProps.config}
				preview={pageProps.preview}
				environment={environment}
				user={pageProps.user}
				facilityCode={pageProps?.facilityCode}
				defaultToCdn={pageProps?.config?.featureFlags?.defaultToCdn}
				deviceType={pageProps.deviceType}
				facilityContext={pageProps.facilityContext}
				hideHeaderFacilityLocator={featureFlags?.hideHeaderFacilityLocator}
				stateCodeFromHeaders={pageProps.stateCodeFromHeaders}
				phoneSwapOn={phoneSwapOn}
			>
				{getLayout(<Component {...pageProps} />)}
			</ContextProviders>
		</Session>
	);
};

export default App;
